/**
 * Reschedule a single match to the next available slot (availability-aware).
 * Used when a player declines the calendar invite or is marked unavailable.
 */

import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  updateDoc,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatDate } from '../utils/dateFormat';
import {
  generateSlots,
  migrateScheduleConfig,
} from '../utils/scheduleStrategy';
import { getAvailabilityForPlayers, createAvailabilityChecker } from './playerAvailabilityService';
import { ensureVenueBlockForMatch } from './venueBlockService';
import { toUtcMs } from '../utils/dateTimeUtils';

const LOG_TYPES = { RESCHEDULE_COMPLETED: 'reschedule_completed' };

function toPlainDate(dateOrTimestamp) {
  const ms = toUtcMs(dateOrTimestamp);
  return ms != null ? new Date(ms) : null;
}

function getSlotKey(dateOrTimestamp, timeStr) {
  const d = toPlainDate(dateOrTimestamp);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}|${(timeStr || '').trim()}`;
}

function getParticipantIds(match) {
  if (match.matchType === 'doubles') {
    return [...(match.team1Players || []), ...(match.team2Players || [])];
  }
  return [match.player1Id, match.player2Id].filter(Boolean);
}

/**
 * Reschedule one match to the next available slot where all participants are available.
 * Uses tournament schedule config and player availability (when tournament has askPlayerAvailability).
 * @param {string} matchId
 * @returns {Promise<{ rescheduled: boolean, newDate?: Date, newTime?: string, error?: string }>}
 */
export async function rescheduleMatchToNextAvailableSlot(matchId) {
  const matchSnap = await getDoc(doc(db, 'matches', matchId));
  if (!matchSnap.exists()) {
    return { rescheduled: false, error: 'Match not found' };
  }
  const match = { id: matchSnap.id, ...matchSnap.data() };
  const tournamentId = match.tournamentId;
  if (!tournamentId) {
    return { rescheduled: false, error: 'Match has no tournament' };
  }

  const tSnap = await getDoc(doc(db, 'tournaments', tournamentId));
  if (!tSnap.exists()) {
    return { rescheduled: false, error: 'Tournament not found' };
  }
  const tournament = { id: tSnap.id, ...tSnap.data() };
  const config = migrateScheduleConfig(tournament.scheduleConfig || {});
  const hasSlots =
    config.scheduleStart != null &&
    config.scheduleEnd != null &&
    Array.isArray(config.weekdays) &&
    config.weekdays.length > 0;
  if (!hasSlots) {
    return { rescheduled: false, error: 'Tournament has no schedule config' };
  }

  const participantIds = getParticipantIds(match);
  let availabilityChecker = null;
  if (tournament.askPlayerAvailability && participantIds.length > 0) {
    const availabilityByPlayer = await getAvailabilityForPlayers(tournamentId, participantIds);
    availabilityChecker = createAvailabilityChecker(availabilityByPlayer);
  }

  const otherSnap = await getDocs(
    query(
      collection(db, 'matches'),
      where('tournamentId', '==', tournamentId),
      where('status', 'in', ['scheduled', 'in-progress'])
    )
  );
  const allScheduled = otherSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const takenSlots = new Set();
  for (const m of allScheduled) {
    if (m.id === matchId) continue;
    if (m.scheduledDate) {
      const key = getSlotKey(m.scheduledDate, m.scheduledTime);
      if (key) takenSlots.add(key);
    }
  }

  const minSlots = 80;
  const allSlots = generateSlots(config, minSlots);
  const currentDate = toPlainDate(match.scheduledDate);
  const currentTimeMins = (match.scheduledTime || '')
    .toString()
    .split(':')
    .reduce((h, m, i) => h + (i === 0 ? parseInt(m, 10) * 60 : parseInt(m, 10) || 0), 0);
  let nextSlot = null;
  for (const slot of allSlots) {
    const slotDate = slot.date instanceof Date ? slot.date : new Date(slot.date);
    const slotDay = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate()).getTime();
    const slotTimeMins = (slot.time || '').toString().split(':').reduce((h, m, i) => h + (i === 0 ? parseInt(m, 10) * 60 : parseInt(m, 10) || 0), 0);
    const currentDay = currentDate ? new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime() : 0;
    const isAfter = slotDay > currentDay || (slotDay === currentDay && slotTimeMins > currentTimeMins);
    if (!isAfter) continue;
    const key = getSlotKey(slot.date, slot.time);
    if (takenSlots.has(key)) continue;
    if (typeof availabilityChecker === 'function' && !availabilityChecker(slot, participantIds)) continue;
    nextSlot = slot;
    break;
  }
  if (!nextSlot) {
    return { rescheduled: false, error: 'No next available slot found' };
  }

  const slotDate = nextSlot.date instanceof Date ? new Date(nextSlot.date) : new Date(nextSlot.date);
  const durationMins = config.matchDuration || 30;
  const venueId = match.venueId || tournament.defaultVenueId || null;
  const updatedMatch = {
    ...match,
    scheduledDate: slotDate,
    scheduledTime: nextSlot.time,
    venueId: match.venueId || venueId,
  };
  let venueBlockId = null;
  if (updatedMatch.venueId) {
    venueBlockId = await ensureVenueBlockForMatch(updatedMatch, durationMins);
  }

  const updatePayload = {
    scheduledDate: slotDate,
    scheduledTime: nextSlot.time,
    updatedAt: Timestamp.now(),
  };
  if (venueBlockId != null) updatePayload.venueBlockId = venueBlockId;
  if (updatedMatch.venueId) updatePayload.venueId = updatedMatch.venueId;

  const oldDateObj = toPlainDate(match.scheduledDate);
  const oldTime = (match.scheduledTime || '').toString();

  await updateDoc(doc(db, 'matches', matchId), updatePayload);
  await addDoc(collection(db, 'matchUpdateLogs'), {
    matchId,
    type: LOG_TYPES.RESCHEDULE_COMPLETED,
    oldDate: oldDateObj ? formatDate(oldDateObj) : '',
    oldTime,
    newDate: formatDate(slotDate),
    newTime: nextSlot.time,
    performedBy: null,
    performedByName: 'Auto-reschedule (next slot by availability)',
    performedAt: Timestamp.now(),
  });

  return { rescheduled: true, newDate: slotDate, newTime: nextSlot.time };
}
